import { usePermissions } from "@podtrac/common/accounts";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash-es/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaPlus, FaSortAlphaDown, FaSortNumericDownAlt } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import ShowIcon from "../shared/ShowIcon";
import { useAccountID } from "../store/account";
import { useShowID } from "../store/show";
import { pageSize, useShows, useShowsPages } from "../store/shows";
import { ShowMenu } from "./ShowNav";
import { useContext } from "react";
import { FilterContext, SortContext, filterAtom, sortAtom } from "./Dashboard";
import styles from "./ShowsNav.module.scss";
import * as classnames from "classnames";
import { useAtom } from "jotai";

export function ShowsNav() {

    const { accountID } = useParams();

    // check for any shows without filter
    const query = useShows(accountID, "T", "", 0);
    const { data: shows, isLoading } = query;

    return shows?.totalItems > 0 ? <ShowsNavInner /> : <div style={{ flex: 1 }} />;
}

function ShowsNavInner() {
    const accountID = useAccountID();
    const showID = useShowID();

    const [sort, setSort] = useAtom(sortAtom); // useContext(SortContext);
    const [filter, setFilter] = useAtom(filterAtom); // useContext(FilterContext);
    //const inputRef = useRef();

    const [pages, setPages] = useState([0]);
    const queries = useShowsPages(accountID, sort, filter, pages);
    const itemCount = queries[0]?.data?.totalItems;
    const items = pages.reduce(
        (items, pageIndex, i) => {
            const data = queries[i].data;
            if (data) {
                items.itemCount = data.totalItems;
                items.pages[pageIndex] = data.items;
            }
            return items;
        },
        { pages: [] }
    );

    const onChange = useCallback(
        debounce(
            virtualizer => {
                //console.log(virtualizer);
                const { range } = virtualizer;
                if (!range) return;
                const { startIndex, endIndex } = range;
                //console.log({ overscanStartIndex, overscanStopIndex });
                const indexes = [];
                for (
                    let pageIndex = Math.floor(startIndex / pageSize);
                    pageIndex <= Math.floor(endIndex / pageSize);
                    pageIndex++
                ) {
                    //console.log({ pageIndex, indexes, loaded });
                    indexes.push(pageIndex);
                }
                if (indexes.length) {
                    setPages(state => {
                        for (let pageIndex of indexes) {
                            if (!state.includes(pageIndex)) {
                                const pages = Array.from(new Set([...state, ...indexes]));
                                console.log("pages", pages);
                                return pages;
                            }
                        }

                        return state;
                    });
                }
            },
            200, // repeated calls before this many ms have elapsed are swallowed
            {
                leading: true, // but fire the first call immediately
                maxWait: 1000, // and let one through every maxWait ms even if the timer hasn't expired
                trailing: true // and make sure the last call is fired after the timer expires
            }
        ),
        [accountID, pageSize, filter]
    );

    useEffect(() => {
        setFilter("");
        //inputRef.current.value = "";
    }, [accountID]);

    // The scrollable element for your list
    const parentRef = React.useRef();

    // The virtualizer
    const rowVirtualizer = useVirtualizer({
        count: itemCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 32,
        overscan: 20,
        onChange
    });

    return (
        <>
            <h1 className="item">
                <span className="label">
                    Shows {itemCount >= 0 ? `(${Intl.NumberFormat().format(itemCount)})` : ""}
                </span>{" "}
                <AddShowButton />
            </h1>
            {/*
            <form
                style={{ display: "flex", margin: "0 1rem" }}
                onSubmit={event => {
                    event.preventDefault();
                    setFilter(inputRef.current.value);
                }}
            >
                <input type="text" ref={inputRef} defaultValue={filter} style={{ flex: 1, fontSize: "0.75rem" }} placeholder="filter by title" />
            </form>
            <SortButtons />
            */}
            <div
                ref={parentRef}
                style={{
                    flex: 1,
                    overflow: "auto",
                    backgroundColor: "#ebebeb"
                }}
            >
                {/* The large inner element to hold all of the items */}
                <ul
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative"
                    }}
                >
                    {/* Only the visible items in the virtualizer, manually positioned to be in view */}
                    {rowVirtualizer.getVirtualItems().map(virtualItem => {
                        const pageIndex = Math.floor(virtualItem.index / pageSize);
                        const i = virtualItem.index % pageSize;
                        const show = items?.pages?.[pageIndex]?.[i];

                        return (
                            <li
                                key={virtualItem.key}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: `${virtualItem.size}px`,
                                    transform: `translateY(${virtualItem.start}px)`
                                }}
                                className={`item hover${
                                    showID && showID == show?.showID ? " active" : ""
                                }`}
                            >
                                {show ? (
                                    <Link
                                        className="label"
                                        to={`shows/${show.showID}`}
                                        title={show.showName}
                                    >
                                        <ShowIcon showImageUrl={show.showImageUrl} />
                                        <span>{show.showName}</span>
                                    </Link>
                                ) : (
                                    <a className="label">
                                        <ShowIcon
                                            style={{ filter: "grayscale(100%) opacity(50%)" }}
                                        />
                                        <span>…</span>
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
}

function ShowsNavOLD() {
    const accountID = useAccountID();
    const showID = useShowID();
    const { data: shows } = useShows(accountID) || {};

    return (
        <>
            {accountID && shows ? (
                <NavHeading>
                    <NavLabel>Shows</NavLabel> <AddShowButton />
                </NavHeading>
            ) : null}
            <ul style={{ overflowY: "auto", flexGrow: 1 }}>
                {accountID &&
                    shows &&
                    shows
                        .filter(s => s.active)
                        .map(s => (
                            <NavItem
                                key={s.showID}
                                className={`item hover${showID == s.showID ? " active" : ""}`}
                            >
                                <Link className="label" to={`shows/${s.showID}`} title={s.showName}>
                                    <ShowIcon showImageUrl={s.showImageUrl} />
                                    <span>{s.showName}</span>
                                </Link>
                                <ShowMenu accountID={accountID} showID={s.showID} show={s} />
                            </NavItem>
                        ))}
            </ul>
        </>
    );
}

function AddShowButton() {
    const navigate = useNavigate();
    const { accountID } = useParams();
    const { data: permissions } = usePermissions(accountID);
    return permissions?.canAddShow ? (
        <button onClick={() => navigate("add-show")}>
            <FaPlus />
            <span className="tip">Add a Show</span>
        </button>
    ) : (
        false
    );
}

function SortButtons() {
    const [sort, setSort] = useAtom(sortAtom);
    const onChange = event => setSort(event.target.value);
    return <div className={classnames("item", styles.sort)}>
        Sort by
        <label>
            <input name="sort" type="radio" value="T" checked={sort == "T"} onChange={onChange} />
            title
        </label>
        <label>
            <input name="sort" type="radio" value="D" checked={sort == "D"} onChange={onChange} />
            downloads
        </label>
    </div>;
}